import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, replace } from "react-router-dom";
import Cookies from "js-cookie";
import {
  Typography,
  Box,
  Stack,
  Grid,
  Button,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ReminderEditModal from "../Dashboard/Reminders/ReminderEditModal";
import moment from "moment";
import { UserContext } from "../../context/UserContext";
import FlowCard from "./FlowCard";
import MiNiFirmReport from "../Dashboard/FirmListing/MiNiFirmReport";
import InitialContact from "./CaseFlowDetail/InitialContact";
import ThriveModal from "../common/ThriveModal";
import DiscoveryQuestions from "./CaseFlowDetail/DiscoveryQuestions";
import BusinessNeedsAssessment, {
  computeProgress,
} from "./CaseFlowDetail/BusinessNeedsAssessment";
import Interaction from "./Interaction";
import Referral from "./Referral";
import ClientWorkflow, {
  ReferralIdGroups,
  emptyReferralIdGroups,
} from "./ClientWorkflow";
import OutcomeCaptureModal from "./Outcomes/OutcomeCaptureModal";
import {
  CaseType,
  defaultCase,
  UserType,
  defaultUser,
} from "../../interfaces";
import ContentChange from "./CaseFlowDetail/ContentChange";
import ReassignClient from "./ReassignClient";
import ClientReferralSurvey from "./ClientReferralSurvey";
import Confirm from "./Confirm";
import NoticeDialog from "../common/NoticeDialog";
import EmailViewer from "../common/EmailViewer";
import EmailEditor from "../common/EmailEditor";
import SurveyResultViewer from "../common/SurveyResultViewer";
import Add2ndBfc from "./Add2ndBfc";
import MergeCase from "./MergeCase";
import { ProviderReferralClientSurvey } from "../common/EmailBodies/ProviderReferralClientSurvey";
import { ProviderReferralProviderSurvey } from "../common/EmailBodies/ProviderReferralProviderSurvey";
import { DirectEmailEmptyBody } from "../common/EmailBodies/DirectEmailEmptyBody";
import { ResourceReferralNotice } from "../common/EmailBodies/ResourceReferralNotice";
import { Provider2hoursReferral } from "../common/EmailBodies/Provider2hoursReferral";
import { axiosInstance } from "../../utils/networking";
import DataValidationDrawer, {
  needsValidation,
} from "../Dashboard/DataValidations/DataValidationDrawer";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { StyledButtonMain } from "../common/Buttons";
import WebIntakeDetail from "../Dashboard/WebIntakeForms/WebIntakeDetail";
import CaseFileManager from "./CaseFileManager";
import CaseHistory from "./CaseHistory/CaseHistory";
import {
  activitiesToCsv,
  activityCsvFilename,
  downloadCsv,
} from "./activityExport";
import { useViewport } from "../../utils/useViewport";

// A case action below Interaction / Client Detail, as a tablet button or a
// phone "More actions" menu item.
type CaseAction = {
  label: string;
  color: "success" | "warning" | "error";
  onClick: () => void;
};

const Case = () => {
  const { user }: { user: UserType } = useContext(UserContext);
  const { isPhone, isCompact, isTouch } = useViewport();
  const [moreActionsAnchor, setMoreActionsAnchor] =
    useState<HTMLElement | null>(null);
  // The More actions menu only exists in the phone layout: forget its anchor
  // when the screen leaves it (rotation, split view), or it would reopen on
  // its own against a detached button when the phone layout comes back.
  useEffect(() => {
    if (!isPhone) setMoreActionsAnchor(null);
  }, [isPhone]);

  const [dataFlag, setDataFlag] = useState<boolean>(false);
  // Referral ids grouped by kind, derived from the activity that created
  // them: "Referral" → resource referral, "Provider Referral Initiated" → B2B.
  const [referrals, setReferrals] = useState<ReferralIdGroups>(
    emptyReferralIdGroups,
  );
  const [archivedReferrals, setArchivedReferrals] = useState<ReferralIdGroups>(
    emptyReferralIdGroups,
  );
  const [referralNoticeContent, setReferralNoticeContent] = useState({});
  const [providerReferralNoticeOpen, setProviderReferralNoticeOpen] =
    useState(false);
  const [openActivityID, setOpenActivityID] = useState<string>("");

  const [cardOpen, setCardOpen] = useState<boolean>(false);
  const [activityCardOpen, setActivityCardOpen] =
    React.useState<boolean>(false);
  const [referralNoticeOpen, setReferralNoticeOpen] =
    React.useState<boolean>(false);
  const [reassignClientOpen, setReassignClientOpen] =
    React.useState<boolean>(false);
  const [addBfcOpen, setAddBfcOpen] = React.useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [confirmAction, setConfirmAction] = useState<
    ((id: string) => void) | null
  >(null);
  const [openReferral, setOpenReferral] = useState({});
  const [openCardTitle, setOpenCardTitle] = useState<string>("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const caseID: string = queryParams.get("id") as string;
  const firmIdParam: string | null = queryParams.get("firmId");
  const activityID: string | null = queryParams.get("activity");
  const focusActionItemId: string | null = queryParams.get("action_item");
  const focusReminderId: string | null = queryParams.get("reminder");
  const focusReferralId: string | null = queryParams.get("referral");
  queryParams.delete("activity");
  // `/case?firmId=<firmId>` is resolved to the canonical `/case?id=<caseId>`
  // before any of the case-scoped fetches below run.
  const [firmResolveState, setFirmResolveState] = useState<
    "idle" | "resolving" | "error"
  >(!caseID && firmIdParam ? "resolving" : "idle");

  const [theCase, setTheCase] = useState<CaseType>(defaultCase);
  const [creator, setCreator] = useState<UserType>(defaultUser);
  const [secondaryBfc, setSecondaryBfc] = useState<UserType>(defaultUser);
  const [firm, setFirm] = useState<any>({});
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [activity, setActivity] = useState<any>([]);
  const [bnaProgress, setBnaProgress] = useState<{
    filled: number;
    total: number;
  } | null>(null);
  const [initialMap, setInitialMap] = useState<{
    [key: string]: { init: string; full: string };
  }>({});
  const [providerSurveyOpen, setProviderSurveyOpen] = useState<boolean>(false);
  const [clientSurveyOpen, setClientSurveyOpen] = useState<boolean>(false);
  const [directEmailOpen, setDirectEmailOpen] = useState<boolean>(false);
  const [mergeCaseOpen, setMergeCaseOpen] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<any>({});
  const [validationDrawerOpen, setValidationDrawerOpen] = useState(false);
  // Auto-open the validation modal once per firm per page visit; refetches
  // triggered by dataFlag must not re-open it after the user dismisses it.
  // Never on touch devices: there the header button is the way in.
  const autoValidationShownFor = useRef<string | null>(null);
  const [selectedWebIntake, setSelectedWebIntake] = useState<any>(null);
  const [outcomeCaptureOpen, setOutcomeCaptureOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    navigate(
      {
        pathname: window.location.pathname,
        search: queryParams.toString(),
      },
      { replace: true },
    );
  }, []);

  // Resolve `/case?firmId=` -> `/case?id=` (replace), preserving `activity`.
  useEffect(() => {
    if (caseID || !firmIdParam) return;
    let cancelled = false;
    axiosInstance
      .get(`/cases?firm_id=${firmIdParam}`)
      .then((res) => {
        if (cancelled) return;
        const resolvedCaseId = res?.data?.case?._id;
        if (resolvedCaseId) {
          navigate(
            `/case?id=${resolvedCaseId}` +
              (activityID ? `&activity=${activityID}` : ""),
            { replace: true },
          );
        } else {
          setFirmResolveState("error");
        }
      })
      .catch(() => {
        if (!cancelled) setFirmResolveState("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeCase = (id: string) => {
    axiosInstance
      .post(`/cases/updateStatus`, {
        id: id,
        status: "completed",
      })
      .then((res) => {
        navigate("/dashboard");
      });
  };

  const deleteCase = (id: string) => {
    axiosInstance
      .post(`/cases/updateStatus`, {
        id: id,
        status: "deleted",
      })
      .then((res) => {
        navigate("/dashboard");
      });
  };

  const reopenCase = (id: string) => {
    axiosInstance
      .post(`/cases/updateStatus`, {
        id: id,
        status: "open",
      })
      .then((res) => {
        navigate("/dashboard");
      });
  };

  const getUsers = (theCase: any) => {
    axiosInstance.get(`/user?id=${theCase.user}`).then((res) => {
      setCreator(res.data.user);
    });

    if (theCase.user_2nd) {
      axiosInstance.get(`/user?id=${theCase.user_2nd}`).then((res) => {
        setSecondaryBfc(res.data.user);
      });
    }
  };

  const getFirmById = (id: string) => {
    axiosInstance.get(`/firms/id?id=${id}`).then((res) => {
      setFirm(res.data.firm);
    });
  };

  const getReferrals = (activity: any): ReferralIdGroups => {
    const groups: ReferralIdGroups = { resource: [], b2b: [] };
    activity.forEach((item: any) => {
      if (!item.referral_id) return;
      const bucket =
        item.activity_type === "Provider Referral Initiated"
          ? groups.b2b
          : groups.resource;
      if (!bucket.includes(item.referral_id)) bucket.push(item.referral_id);
    });
    return groups;
  };

  const getInitialMap = (ids: string[]) => {
    let tempInitialMap: { [key: string]: { init: string; full: string } } = {
      HIS: { init: "HIS", full: "HIS" },
    };
    const clean_ids = ids.filter((element: any) => {
      // Keep only elements that are not null, undefined, or empty strings
      return (
        element !== null &&
        element !== undefined &&
        element !== "" &&
        element !== "HIS"
      );
    });
    axiosInstance.get(`/user?ids=${clean_ids.join(",")}`).then((res) => {
      res.data.user.map((user: any) => {
        tempInitialMap[user._id as string] = {
          init: user["firstName"][0] + user["lastName"][0],
          full: user["firstName"] + " " + user["lastName"],
        };
        return null;
      });
      setInitialMap(tempInitialMap);
    });
  };

  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState(false);

  // Readable export: built server-side so ids resolve to names/subjects.
  const exportReadableCsv = () => {
    setExporting(true);
    axiosInstance
      .get(`/activity/export?case_id=${caseID}`, { responseType: "text" })
      .then((res) => {
        downloadCsv(
          String(res.data).replace(/^\uFEFF/, ""),
          activityCsvFilename(firm["Company Name"]),
        );
      })
      .catch(() => {
        setMessageTitle("Export failed");
        setMessageBody("The activity export could not be created. Please try again.");
        setMessageOpen(true);
      })
      .finally(() => setExporting(false));
  };

  // Raw export: every stored field, straight from the loaded activities.
  const exportRawCsv = () => {
    const userNames: Record<string, string> = {};
    Object.entries(initialMap).forEach(([id, names]) => {
      userNames[id] = names.full;
    });
    downloadCsv(
      activitiesToCsv(activity, userNames),
      activityCsvFilename(firm["Company Name"], new Date(), "raw"),
    );
  };

  const compareByDate = (a: any, b: any) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  };

  const getActivity = (theCase: any) => {
    axiosInstance.get(`/activity?case_id=${theCase._id}`).then((res) => {
      const sortedActivity = res.data.activity.sort(compareByDate);
      setActivity(sortedActivity);
      const initials: string[] = Array.from(
        new Set(res.data.activity.map((item: any) => item.user_id)),
      );

      getInitialMap(initials);
      setReferrals(
        getReferrals(
          res.data.activity.filter((item: any) => {
            return (
              item.activity_type === "Referral" ||
              item.activity_type === "Provider Referral Initiated"
            );
          }),
        ),
      );
      const webIntakeActivity = sortedActivity.find(
        (item: any) => item.activity_type === "Web Intake Assigned",
      );
      if (webIntakeActivity) {
        const webIntakeEntryId = theCase["firmRecord"]["Web Intake Entry ID"];
        if (webIntakeEntryId) {
          axiosInstance
            .get(`/webintake?entry_id=${webIntakeEntryId}`)
            .then((res) => {
              const webIntakeData = res.data.webintake;
              setSelectedWebIntake(webIntakeData);
            });
        }
      }

      // Fetch BNA progress for FlowCard indicator
      const bnaActivity = sortedActivity
        .filter((a: any) => a.activity_type === "Business Assessment")
        .sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
      if (bnaActivity.length > 0 && bnaActivity[0].survey_result_id) {
        axiosInstance
          .get(
            `/survey/result?survey_result_id=${bnaActivity[0].survey_result_id}`,
          )
          .then((r) => {
            if (r.status === 200 && r.data.survey_result) {
              setBnaProgress(computeProgress({ ...r.data.survey_result }));
            }
          })
          .catch(() => {});
      }
    });
  };

  const getDeletedActivity = (id: string) => {
    axiosInstance.get(`/activity?case_id=${id}&status=deleted`).then((res) => {
      setArchivedReferrals(
        getReferrals(
          res.data.activity.filter((item: any) => {
            return (
              item.activity_type === "Referral" ||
              item.activity_type === "Provider Referral Initiated"
            );
          }),
        ),
      );
    });
  };

  // One big Referral.tsx card (notice/survey buttons, status stepper). The
  // cards live under the Resource / B2B Referrals tabs of ClientWorkflow;
  // the modals they open stay owned by this page.
  const renderReferralCard = (referral: string) => (
    <Box
      key={referral}
      sx={{
        backgroundColor: "white",
        marginBottom: "25px",
        borderRadius: "5px",
        overflow: "hidden",
        boxShadow:
          "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
      }}
    >
      <Referral
        setProviderSurveyOpen={setProviderSurveyOpen}
        setClientSurveyOpen={setClientSurveyOpen}
        setReferralNoticeOpen={setReferralNoticeOpen}
        setReferralNoticeContent={setReferralNoticeContent}
        providerReferralNoticeOpen={providerReferralNoticeOpen}
        setProviderReferralNoticeOpen={setProviderReferralNoticeOpen}
        setOpenReferral={setOpenReferral}
        setActivityCardOpen={setActivityCardOpen}
        setOpenActivityID={setOpenActivityID}
        setSelectedProvider={setSelectedProvider}
        user={user}
        referral={referral}
        dataFlag={dataFlag}
        setDataFlag={setDataFlag}
        activity={activity.filter((item: any) => item.referral_id === referral)}
        case={theCase}
        firm={firm}
      ></Referral>
    </Box>
  );

  React.useEffect(() => {
    if (!caseID) return;
    axiosInstance.get(`/cases?id=${caseID}`).then((res) => {
      setTheCase(res.data.case);
      getUsers(res.data.case);
      getFirmById(res.data.case.firm);
      getActivity(res.data.case);
      getDeletedActivity(res.data.case._id);
    });
  }, [dataFlag, caseID]);

  useEffect(() => {
    if (!firm?._id) return;
    if (autoValidationShownFor.current === firm._id) return;
    autoValidationShownFor.current = firm._id;
    if (!isTouch && needsValidation(firm)) {
      setValidationDrawerOpen(true);
    }
  }, [firm, isTouch]);

  const ActivityModalContent = (id: string) => {
    const selectedActivity = activity.find((activityItem: any) => {
      return activityItem.activity_id === id;
    });
    if (selectedActivity) {
      switch (selectedActivity["activity_type"]) {
        case "Initial Contact (edit)":
          return (
            <ContentChange
              setCardOpen={setActivityCardOpen}
              caseID={caseID}
              activity={selectedActivity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
            ></ContentChange>
          );
        case "Initial Contact":
          return (
            <InitialContact
              setCardOpen={setActivityCardOpen}
              caseID={caseID}
              activity={activity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
            ></InitialContact>
          );

        // case "Discovery Questions":
        //   return (
        //     <DiscoveryQuestions
        //       setCardOpen={setActivityCardOpen}
        //       case={theCase}
        //       activity={activity}
        //       dataFlag={dataFlag}
        //       setDataFlag={setDataFlag}
        //     ></DiscoveryQuestions>
        //   );

        case "Client Referral Survey (by Client)":
          return (
            <ClientReferralSurvey
              setSurveyOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></ClientReferralSurvey>
          );

        case "B2B Client Survey (by Client)":
          return (
            <SurveyResultViewer
              setSurveyOpen={setActivityCardOpen}
              activity={selectedActivity}
              title={"B2B Client Survey Result"}
            ></SurveyResultViewer>
          );

        case "B2B Provider Survey (by Provider)":
          return (
            <SurveyResultViewer
              setSurveyOpen={setActivityCardOpen}
              activity={selectedActivity}
              title={"B2B Provider Survey Result"}
            ></SurveyResultViewer>
          );

        // case "Discovery Questions (edit)":
        //   return (
        //     <ContentChange
        //       setCardOpen={setActivityCardOpen}
        //       case={theCase}
        //       activity={selectedActivity}
        //       dataFlag={dataFlag}
        //       setDataFlag={setDataFlag}
        //     ></ContentChange>
        //   );

        case "Business Assessment":
          return (
            <BusinessNeedsAssessment
              setCardOpen={setActivityCardOpen}
              case={theCase}
              firm={firm}
              activity={activity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
              onProgressChange={(f: number, t: number) =>
                setBnaProgress({ filled: f, total: t })
              }
            ></BusinessNeedsAssessment>
          );

        case "Business Assessment (edit)":
          return (
            <BusinessNeedsAssessment
              setCardOpen={setActivityCardOpen}
              case={theCase}
              firm={firm}
              activity={activity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
              onProgressChange={(f: number, t: number) =>
                setBnaProgress({ filled: f, total: t })
              }
            ></BusinessNeedsAssessment>
          );

        // The Business Action Plan was retired. Its historical rows stay in the
        // audit log, but the plan content itself is no longer rendered — goals
        // and tasks live in the Action Items tab of the workflow panel.
        case "Business Action Plan":
        case "Business Action Plan (edit)":
        case "Action Item":
        case "Action Item (edit)":
          return (
            <Box sx={{ padding: "16px", maxWidth: "560px" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Action Items
              </Typography>
              <Typography sx={{ marginTop: "8px" }}>
                Goals and tasks are managed in the Action Items tab of the
                workflow panel on this case. The Business Action Plan form has
                been retired.
              </Typography>
              <Button
                variant="contained"
                sx={{ marginTop: "16px" }}
                onClick={() => setActivityCardOpen(false)}
              >
                Close
              </Button>
            </Box>
          );

        case "Referral (edit)":
          return (
            <ContentChange
              setCardOpen={setActivityCardOpen}
              case={theCase}
              activity={selectedActivity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
            ></ContentChange>
          );

        case "Firm Validation":
          return (
            <ContentChange
              setCardOpen={setActivityCardOpen}
              case={theCase}
              activity={selectedActivity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
            ></ContentChange>
          );

        case "Edit Firm":
          return (
            <ContentChange
              setCardOpen={setActivityCardOpen}
              case={theCase}
              activity={selectedActivity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
            ></ContentChange>
          );

        case "Email Communication":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Referral Notice":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "B2B Client Survey Email":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "B2B Provider Survey Email":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Referral Survey":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Referral Survey Email":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Provider Referral Initiated":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Providers Referral Email":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Provider Referral Notice Email":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Event Invitation":
          return (
            <EmailViewer
              setEditorOpen={setActivityCardOpen}
              activity={selectedActivity}
            ></EmailViewer>
          );

        case "Upload File":
          return (
            <CaseFileManager
              caseID={caseID}
              activity={activity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
              setCardOpen={setActivityCardOpen}
            />
          );

        case "Delete File":
          return (
            <CaseFileManager
              caseID={caseID}
              activity={activity}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
              setCardOpen={setActivityCardOpen}
            />
          );

        default:
          return (
            <Interaction
              displayTitle={"Edit Interaction"}
              setCardOpen={setActivityCardOpen}
              user={user}
              firm={firm}
              case={theCase}
              dataFlag={dataFlag}
              setDataFlag={setDataFlag}
              activity={selectedActivity}
            ></Interaction>
          );
      }
    }
  };

  const FlowCardModalContent = (title: string) => {
    switch (title) {
      case "INITIATE CONTACT WITH OUTREACH":
        return (
          <InitialContact
            setCardOpen={setCardOpen}
            caseID={caseID}
            activity={activity}
            dataFlag={dataFlag}
            setDataFlag={setDataFlag}
          ></InitialContact>
        );

      // case "ASK DISCOVERY QUESTIONS and RECORD ENVIRONMENTAL INFORMATION":
      //   return (
      //     <DiscoveryQuestions
      //       setCardOpen={setCardOpen}
      //       case={theCase}
      //       activity={activity}
      //       dataFlag={dataFlag}
      //       setDataFlag={setDataFlag}
      //     ></DiscoveryQuestions>
      //   );

      case "CONDUCT BUSINESS NEEDS ASSESSMENT":
        return (
          <BusinessNeedsAssessment
            setCardOpen={setCardOpen}
            case={theCase}
            firm={firm}
            activity={activity}
            dataFlag={dataFlag}
            setDataFlag={setDataFlag}
            onProgressChange={(f: number, t: number) =>
              setBnaProgress({ filled: f, total: t })
            }
          ></BusinessNeedsAssessment>
        );

      case "Interaction":
        return (
          <Interaction
            setCardOpen={setCardOpen}
            user={user}
            firm={firm}
            case={theCase}
            dataFlag={dataFlag}
            setDataFlag={setDataFlag}
          ></Interaction>
        );

      case "View Web Intake Form Information":
        return (
          <WebIntakeDetail
            setOpen={setCardOpen}
            submission={selectedWebIntake}
            actionMode={false}
          ></WebIntakeDetail>
        );
      default:
        break;
    }
  };

  const Remove2ndBfc = () => {
    axiosInstance
      .post(`/cases/remove2ndBfc`, {
        caseId: caseID,
      })
      .then((res: any) => {
        setDataFlag(!dataFlag);
      });
  };

  const openInteraction = () => {
    setCardOpen(true);
    setOpenCardTitle("Interaction");
  };

  const openClientDetail = () => {
    window.open(`/firmReportPdf?id=${theCase._id}`, "_blank");
  };

  // MergeCase shows the irreversible-merge warning as a confirm step once a
  // case has been picked.
  const openMergeCase = () => {
    setMergeCaseOpen(true);
  };

  const confirmUnassign = () => {
    setConfirmMessage("Are you sure to delete the Case?");
    setConfirmOpen(true);
    setConfirmAction(() => () => deleteCase(caseID));
  };

  const isOpen = theCase.status === "open";
  // Same order and visibility rules as the desktop action column.
  const secondaryActions: CaseAction[] = [];
  if (isOpen) {
    secondaryActions.push(
      theCase.user_2nd
        ? { label: "Unassign 2nd SBC", color: "success", onClick: Remove2ndBfc }
        : {
            label: "Add 2nd SBC",
            color: "success",
            onClick: () => setAddBfcOpen(true),
          },
      {
        label: "Close Case",
        color: "success",
        onClick: () => setOutcomeCaptureOpen(true),
      },
      {
        label: "Reassign Client",
        color: "success",
        onClick: () => setReassignClientOpen(true),
      },
    );
  }
  if (theCase.status === "completed") {
    secondaryActions.push({
      label: "Reopen Case",
      color: "success",
      onClick: () => reopenCase(caseID),
    });
  }
  if (isOpen) {
    secondaryActions.push(
      { label: "Merge Case", color: "warning", onClick: openMergeCase },
      { label: "Unassign", color: "error", onClick: confirmUnassign },
    );
  }

  const hasWebIntake = activity.some(
    (item: any) => item.activity_type === "Web Intake Assigned",
  );
  // Below md the flow cards are compact: one row on tablets, two per row on
  // phones.
  const flowCardCols = { xs: 6, sm: hasWebIntake ? 3 : 4, md: 6 };

  if (!caseID && firmIdParam) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          minHeight: "300px",
          padding: "40px",
        }}
      >
        {firmResolveState === "error" ? (
          <>
            <Alert severity="warning" sx={{ maxWidth: "520px" }}>
              This firm has no active case. Open the firm from the firm list to
              start one.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/dashboard/firms")}
            >
              Back to Firms
            </Button>
          </>
        ) : (
          <>
            <CircularProgress />
            <Typography variant="body1">Opening case…</Typography>
          </>
        )}
      </Box>
    );
  }

  if (activityID && openActivityID === "") {
    ActivityModalContent(activityID);
    setOpenActivityID(activityID);
    setActivityCardOpen(true);
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          maxWidth: "98%",
          margin: "auto",
          marginBottom: "20px",
          borderRadius: "5px",
          minHeight: "400px",
          boxShadow:
            "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);",
        }}
      >
        {/* From lg the reminder and validation controls sit absolutely over
            the centred name; below lg all three share one flex row. */}
        <Stack
          sx={{
            color: "white",
            backgroundColor:
              theCase.status === "open" ? "#0F95B3" : "#8a8a8aff",
            borderRadius: "5px 5px 0 0",
            height: { lg: "50px" },
            minHeight: "50px",
            marginBottom: "30px",
            width: "100%",
            justifyContent: "center",
            position: "relative",
            gap: { xs: 1, lg: 0 },
            padding: { xs: "4px 8px 4px 16px", lg: 0 },
          }}
          direction={"row"}
          alignItems={"center"}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: { xs: "wrap", lg: "nowrap" },
              columnGap: { xs: 1, lg: 0 },
              flex: { xs: "1 1 auto", lg: "0 1 auto" },
              minWidth: { xs: 0, lg: "auto" },
              textAlign: { xs: "left", lg: "inherit" },
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "24px", sm: "30px", lg: "36px" },
                lineHeight: { xs: 1.25, lg: 1.5 },
                overflowWrap: { xs: "anywhere", lg: "normal" },
              }}
            >
              {firm["Company Name"]}{" "}
            </Typography>
            <Typography sx={{ fontSize: "20px" }}>
              {firm["do_not_contact"] === "Yes" ? "(Do Not Contact)" : ""}{" "}
            </Typography>
          </Box>
          <Tooltip title="Set a reminder">
            <IconButton
              sx={{
                position: { lg: "absolute" },
                right: { lg: "200px" },
                color: "white",
              }}
              onClick={() => setReminderModalOpen(true)}
            >
              <AccessAlarmIcon />
            </IconButton>
          </Tooltip>
          {isPhone ? (
            <Tooltip
              title={
                needsValidation(firm) ? "Need validation" : "Data validated"
              }
            >
              <IconButton
                aria-label={
                  needsValidation(firm) ? "Need validation" : "Data validated"
                }
                sx={{
                  color: "white",
                  backgroundColor: "#ae2c6f",
                  // Same colours as the text button; no sticky hover on touch.
                  "&:hover": {
                    backgroundColor: "#fdb814",
                    "@media (hover: none)": { backgroundColor: "#ae2c6f" },
                  },
                }}
                onClick={() => setValidationDrawerOpen(true)}
              >
                {needsValidation(firm) ? <WarningAmberIcon /> : <CheckIcon />}
              </IconButton>
            </Tooltip>
          ) : needsValidation(firm) ? (
            <StyledButtonMain
              variant="contained"
              color="warning"
              sx={{
                position: { lg: "absolute" },
                right: { lg: "20px" },
                flexShrink: 0,
              }}
              onClick={() => {
                setValidationDrawerOpen(true);
              }}
            >
              Need Validation
            </StyledButtonMain>
          ) : (
            <StyledButtonMain
              variant="contained"
              color="success"
              sx={{
                position: { lg: "absolute" },
                right: { lg: "20px" },
                flexShrink: 0,
              }}
              onClick={() => {
                setValidationDrawerOpen(true);
              }}
            >
              Data Validated
            </StyledButtonMain>
          )}
        </Stack>

        <ReminderEditModal
          open={reminderModalOpen}
          onClose={() => setReminderModalOpen(false)}
          onSaved={() => setReminderModalOpen(false)}
          initial={{
            title: `Follow up: ${firm["Company Name"] || ""}`,
            case_id: caseID,
            firm_name: firm["Company Name"] || null,
            source: { kind: "manual" },
          }}
        />


        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{ marginTop: 1, padding: { xs: "0 16px", md: "0 0 0 16px" } }}
            >
              <CaseHistory
                activities={activity}
                referrals={[...referrals.resource, ...referrals.b2b]}
                caseId={caseID}
                caseStart={theCase.date}
                caseOwnerId={theCase.user}
                initialMap={initialMap}
                onSelect={(id: string) => {
                  setActivityCardOpen(true);
                  setOpenActivityID(id);
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ display: { md: "none" } }}>
              Process Flow
            </Typography>
            <Grid container spacing={2} sx={{ padding: "10px" }}>
              <Grid item {...flowCardCols}>
                <FlowCard
                  compact={isCompact}
                  displayTitle={"CASE CREATED"}
                  title={"CREATE CLIENT VISIT CASE"}
                  subtitle={
                    "You are the SBC working with this firm, assigned via a Firm Visit Plan"
                  }
                  type={"Case Created"}
                  activity={activity}
                  setCardOpen={() => {}}
                  setOpenCardTitle={setOpenCardTitle}
                  disable={theCase.status !== "open"}
                />
              </Grid>
              <Grid item {...flowCardCols}>
                <FlowCard
                  compact={isCompact}
                  displayTitle={"INITIATE CONTACT"}
                  title={"INITIATE CONTACT WITH OUTREACH"}
                  subtitle={
                    "Outreach to the firm via email, phone or in- person"
                  }
                  type={"Initial Contact"}
                  activity={activity}
                  setCardOpen={setCardOpen}
                  setOpenCardTitle={setOpenCardTitle}
                  disable={theCase.status !== "open"}
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <FlowCard
                  disable={true}
                  displayTitle={"OBTAIN DISCOVERY INFO"}
                  title={
                    "ASK DISCOVERY QUESTIONS and RECORD ENVIRONMENTAL INFORMATION"
                  }
                  subtitle={"Learn about firm's current situation"}
                  type={"Discovery Questions"}
                  activity={activity}
                  setCardOpen={setCardOpen}
                  setOpenCardTitle={setOpenCardTitle}
                />
              </Grid> */}
              <Grid item {...flowCardCols}>
                <FlowCard
                  compact={isCompact}
                  displayTitle={"DO NEEDS ASSESSMENT"}
                  title={"CONDUCT BUSINESS NEEDS ASSESSMENT"}
                  subtitle={"Assess firm's needs"}
                  type={"Business Assessment"}
                  activity={activity}
                  setCardOpen={setCardOpen}
                  setOpenCardTitle={setOpenCardTitle}
                  disable={theCase.status !== "open"}
                  progress={bnaProgress}
                />
              </Grid>

              {hasWebIntake && (
                <Grid item {...flowCardCols}>
                  <FlowCard
                    compact={isCompact}
                    displayTitle={"WEB INTAKE FORM"}
                    title={"View Web Intake Form Information"}
                    subtitle={"View Web Intake Form Information"}
                    type={""}
                    activity={activity}
                    setCardOpen={setCardOpen}
                    setOpenCardTitle={setOpenCardTitle}
                    // navigateUrl={"/dashboard/resources"}
                  />
                </Grid>
              )}

              {/* <Grid item xs={12} md={6}>
                <FlowCard
                  disable={true}
                  displayTitle={"ASSESS REFERRALS"}
                  title={"CAPTURE AND RECORD RESULTS OF REFERRALS"}
                  subtitle={
                    "Capture results of referrals and Action Plan activities"
                  }
                  type={"referral_result"}
                  activity={activity}
                  setCardOpen={setCardOpen}
                  setOpenCardTitle={setOpenCardTitle}
                />
              </Grid> */}
              {/* <Grid item xs={12} md={6}>
                <FlowCard
                  disable={true}
                  title={"FOLLOW UP WITH CLIENT"}
                  subtitle={"To determine next steps, if necessary"}
                  type={"follow_up"}
                  activity={activity}
                  setCardOpen={setCardOpen}
                  setOpenCardTitle={setOpenCardTitle}
                />
              </Grid> */}
            </Grid>
          </Grid>
          <Grid item xs={12} md={2}>
            {isCompact ? (
              <Box sx={{ padding: "0 16px 8px", textAlign: "left" }}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ columnGap: 3, rowGap: 0.5, marginBottom: 1.5 }}
                >
                  <Typography variant="body1">
                    Created Date: {moment(theCase.date).format("YYYY-MM-DD")}
                  </Typography>
                  <Typography variant="body1">
                    Primary SBC: {creator.firstName} {creator.lastName}
                  </Typography>
                  {theCase.user_2nd && (
                    <Typography variant="body1">
                      2nd SBC: {secondaryBfc.firstName}{" "}
                      {secondaryBfc.lastName}
                    </Typography>
                  )}
                </Stack>
                {isPhone ? (
                  <Stack spacing={1}>
                    {isOpen && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        onClick={openInteraction}
                      >
                        Interaction
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      fullWidth
                      onClick={openClientDetail}
                    >
                      Client Detail
                    </Button>
                    {secondaryActions.length > 0 && (
                      <>
                        <Button
                          variant="outlined"
                          size="large"
                          fullWidth
                          aria-label="More actions"
                          aria-haspopup="menu"
                          aria-controls={
                            moreActionsAnchor ? "case-more-actions" : undefined
                          }
                          aria-expanded={moreActionsAnchor ? "true" : undefined}
                          endIcon={<ArrowDropDownIcon />}
                          onClick={(event) =>
                            setMoreActionsAnchor(event.currentTarget)
                          }
                        >
                          More actions
                        </Button>
                        <Menu
                          id="case-more-actions"
                          anchorEl={moreActionsAnchor}
                          open={isPhone && Boolean(moreActionsAnchor)}
                          onClose={() => setMoreActionsAnchor(null)}
                        >
                          {secondaryActions.map((action) => (
                            <MenuItem
                              key={action.label}
                              sx={
                                action.color === "error"
                                  ? { color: "error.main" }
                                  : undefined
                              }
                              onClick={() => {
                                setMoreActionsAnchor(null);
                                action.onClick();
                              }}
                            >
                              {action.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {isOpen && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={openInteraction}
                      >
                        Interaction
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      onClick={openClientDetail}
                    >
                      Client Detail
                    </Button>
                    {secondaryActions.map((action) => (
                      <Button
                        key={action.label}
                        variant="contained"
                        color={action.color}
                        size="large"
                        onClick={action.onClick}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Box>
            ) : (
              <Stack spacing={2} sx={{ alignItems: "center" }}>
                {theCase.status === "open" && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ width: "80%", marginTop: "10px" }}
                    onClick={openInteraction}
                  >
                    Interaction
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="success"
                  sx={{ width: "80%" }}
                  onClick={openClientDetail}
                >
                  Client Detail
                </Button>

                <Box>
                  <Stack
                    sx={{
                      padding: "10px",
                      alignItems: "center",
                    }}
                    spacing={1}
                  >
                    <Typography
                      variant="body1"
                      sx={{ textAlign: { md: "left" } }}
                    >
                      Created Date: {moment(theCase.date).format("YYYY-MM-DD")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: { md: "left" } }}
                    >
                      Primary SBC: {creator.firstName} {creator.lastName}
                    </Typography>
                    {theCase.user_2nd && (
                      <Typography
                        variant="body1"
                        sx={{ textAlign: { md: "left" } }}
                      >
                        2nd SBC:{secondaryBfc.firstName} {secondaryBfc.lastName}
                      </Typography>
                    )}

                    {theCase.user_2nd ? (
                      <Button
                        variant="contained"
                        color="success"
                        sx={{
                          width: "100%",
                          display: theCase.status === "open" ? "block" : "none",
                        }}
                        onClick={Remove2ndBfc}
                      >
                        UNASSIGN 2ND SBC
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="success"
                        sx={{
                          width: "100%",
                          display: theCase.status === "open" ? "block" : "none",
                        }}
                        onClick={() => setAddBfcOpen(true)}
                      >
                        ADD 2ND SBC
                      </Button>
                    )}

                    {theCase.status === "open" && (
                      <Button
                        variant="contained"
                        color="success"
                        sx={{ width: "100%" }}
                        onClick={() => setOutcomeCaptureOpen(true)}
                      >
                        Close Case
                      </Button>
                    )}
                    {theCase.status === "open" && (
                      <Button
                        variant="contained"
                        color="success"
                        sx={{ width: "100%" }}
                        onClick={() => setReassignClientOpen(true)}
                      >
                        Reassign Client
                      </Button>
                    )}
                    {theCase.status === "completed" && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          sx={{ width: "100%" }}
                          onClick={() => reopenCase(caseID)}
                        >
                          Reopen Case
                        </Button>
                      </>
                    )}
                    <Button
                      variant="contained"
                      color="warning"
                      sx={{
                        width: "100%",
                        display: theCase.status === "open" ? "block" : "none",
                      }}
                      onClick={openMergeCase}
                    >
                      Merge Case
                    </Button>

                    <Button
                      variant="contained"
                      color="error"
                      sx={{
                        width: "100%",
                        display: theCase.status === "open" ? "block" : "none",
                      }}
                      onClick={confirmUnassign}
                    >
                      Unassign
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Grid>
        </Grid>
        <Box sx={{ padding: "10px 16px" }}>
          <hr />
          <CaseFileManager
            caseID={caseID}
            activity={activity}
            dataFlag={dataFlag}
            setDataFlag={setDataFlag}
          />
        </Box>
      </Box>

      {theCase && theCase._id && (
        <ClientWorkflow
          theCase={theCase}
          firm={firm}
          dataFlag={dataFlag}
          setDataFlag={setDataFlag}
          focusActionItemId={focusActionItemId}
          focusReminderId={focusReminderId}
          focusReferralId={focusReferralId}
          referralCards={referrals}
          archivedReferralCards={archivedReferrals}
          renderReferralCard={renderReferralCard}
        />
      )}

      <OutcomeCaptureModal
        open={outcomeCaptureOpen}
        setOpen={setOutcomeCaptureOpen}
        caseId={caseID}
        firmId={firm?._id || ""}
        onCloseCase={() => closeCase(caseID)}
      />

      <Box
        sx={{
          backgroundColor: "white",
          maxWidth: "98%",
          margin: "auto",
          borderRadius: "5px",
          minHeight: "400px",
          boxShadow:
            "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);",
        }}
      >
        {firm._id ? (
          <MiNiFirmReport
            firm={firm}
            grants={firm.grants}
            user={user}
            dataFlag={dataFlag}
            setDataFlag={setDataFlag}
            setDirectEmailOpen={setDirectEmailOpen}
          />
        ) : (
          <></>
        )}
      </Box>
      <Box
        sx={{
          maxWidth: "98%",
          margin: "16px auto",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          startIcon={
            exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />
          }
          endIcon={<ArrowDropDownIcon />}
          disabled={activity.length === 0 || exporting}
          aria-controls="case-export-menu"
          aria-haspopup="true"
          onClick={(event) => setExportAnchor(event.currentTarget)}
        >
          Export Activity
        </Button>
        <Menu
          id="case-export-menu"
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setExportAnchor(null);
              exportReadableCsv();
            }}
          >
            Readable (CSV)
          </MenuItem>
          <MenuItem
            onClick={() => {
              setExportAnchor(null);
              exportRawCsv();
            }}
          >
            Raw data (CSV)
          </MenuItem>
        </Menu>
      </Box>
      <DataValidationDrawer
        open={validationDrawerOpen}
        onClose={() => setValidationDrawerOpen(false)}
        firm={firm}
        onVerify={(data) => {
          let postData = {
            editedFirm: {
              openCase: caseID,
              _id: data._id,
              Employees: data.Employees ? Number(data.Employees) : 0,
              "Annual Revenue": data["Annual Revenue"]
                ? Number(data["Annual Revenue"])
                : 0,
              "Employees 2025": data["Employees 2025"]
                ? Number(data["Employees 2025"])
                : 0,
              "Annual Revenue 2025": data["Annual Revenue 2025"]
                ? Number(data["Annual Revenue 2025"])
                : 0,
              "Employees 2024": data["Employees 2024"]
                ? Number(data["Employees 2024"])
                : 0,
              "Annual Revenue 2024": data["Annual Revenue 2024"]
                ? Number(data["Annual Revenue 2024"])
                : 0,
              last_validated_at: new Date(),
            },
          };
          axiosInstance.post(`/firms`, postData).then((res) => {
            if (res.status === 200) {
              setValidationDrawerOpen(false);
              setDataFlag((prev) => !prev);
            } else {
              console.log("API Error");
            }
          });
        }}
        onEmailClick={() => setDirectEmailOpen(true)}
      />
      <ThriveModal
        open={cardOpen}
        fullScreenOnPhone
        // setOpen={setCardOpen}
        contentComponent={FlowCardModalContent(openCardTitle)}
      ></ThriveModal>
      <ThriveModal
        open={activityCardOpen}
        height={750}
        fullScreenOnPhone
        // setOpen={setActivityCardOpen}
        contentComponent={ActivityModalContent(openActivityID)}
      ></ThriveModal>

      <ThriveModal
        height={750}
        open={referralNoticeOpen}
        // setOpen={setCardOpen}
        contentComponent={
          <EmailEditor
            theSubject={
              "Oakland Thrive - Referral Notice for " + firm["Company Name"]
            }
            setOpen={setReferralNoticeOpen}
            theCase={theCase}
            theFirm={firm}
            theReferral={referralNoticeContent}
            editorTitle={"Resource Referral Notice"}
            bodyGenerator={ResourceReferralNotice}
            setMessageOpen={setMessageOpen}
            setMessageTitle={setMessageTitle}
            setMessageBody={setMessageBody}
            theBFC={{}}
            theSelectedEvent={{}}
            theSelectedProvider={{}}
          ></EmailEditor>
        }
      ></ThriveModal>

      {/* Modal for provider referral notice */}
      <ThriveModal
        height={750}
        open={providerReferralNoticeOpen}
        // setOpen={setCardOpen}
        contentComponent={
          <EmailEditor
            theSubject={"Oakland Thrive - Provider for " + firm["Company Name"]}
            setOpen={setProviderReferralNoticeOpen}
            theCase={theCase}
            theFirm={firm}
            theReferral={referralNoticeContent}
            editorTitle={"Provider 5 hours Referral Notice"}
            bodyGenerator={Provider2hoursReferral}
            setMessageOpen={setMessageOpen}
            setMessageTitle={setMessageTitle}
            setMessageBody={setMessageBody}
            theBFC={{}}
            theSelectedEvent={{}}
            theSelectedProvider={{}}
          ></EmailEditor>
        }
      ></ThriveModal>

      {/* Modal for B2B client survey */}
      <ThriveModal
        height={750}
        open={clientSurveyOpen}
        // setOpen={setCardOpen}
        contentComponent={
          <EmailEditor
            theSubject={"Oakland Thrive - B2B Client Survey"}
            setOpen={setClientSurveyOpen}
            theCase={theCase}
            theFirm={firm}
            theBFC={creator}
            theReferral={referralNoticeContent}
            editorTitle={"Client Survey - B2B Provider"}
            bodyGenerator={ProviderReferralClientSurvey}
            setMessageOpen={setMessageOpen}
            setMessageTitle={setMessageTitle}
            setMessageBody={setMessageBody}
            theSelectedEvent={{}}
            theSelectedProvider={{}}
          ></EmailEditor>
        }
      ></ThriveModal>
      {/* Modal for B2B provider survey */}
      <ThriveModal
        height={750}
        open={providerSurveyOpen}
        // setOpen={setCardOpen}
        contentComponent={
          <EmailEditor
            theSubject={"Oakland Thrive - B2B Provider Survey"}
            setOpen={setProviderSurveyOpen}
            theCase={theCase}
            theFirm={firm}
            theReferral={referralNoticeContent}
            editorTitle={"Provider Survey - B2B Provider"}
            bodyGenerator={ProviderReferralProviderSurvey}
            setMessageOpen={setMessageOpen}
            setMessageTitle={setMessageTitle}
            setMessageBody={setMessageBody}
            theBFC={{}}
            theSelectedEvent={{}}
            theSelectedProvider={selectedProvider}
          ></EmailEditor>
        }
      ></ThriveModal>
      {/* Modal for direct email */}
      <ThriveModal
        height={750}
        open={directEmailOpen}
        // setOpen={setCardOpen}
        contentComponent={
          <EmailEditor
            theSubject={""}
            setOpen={setDirectEmailOpen}
            theCase={theCase}
            theFirm={firm}
            theReferral={{}}
            editorTitle={"Direct Email"}
            bodyGenerator={DirectEmailEmptyBody}
            setMessageOpen={setMessageOpen}
            setMessageTitle={setMessageTitle}
            setMessageBody={setMessageBody}
            theBFC={{}}
            theSelectedEvent={{}}
            theSelectedProvider={{}}
          ></EmailEditor>
        }
      ></ThriveModal>

      <ThriveModal
        open={addBfcOpen}
        contentComponent={
          <Add2ndBfc
            setAddBfcOpen={setAddBfcOpen}
            currUser={creator}
            caseId={caseID}
          />
        }
      ></ThriveModal>
      <ThriveModal
        open={reassignClientOpen}
        contentComponent={
          <ReassignClient
            setReassignClientOpen={setReassignClientOpen}
            currUser={creator}
            caseId={caseID}
          />
        }
      ></ThriveModal>
      <Confirm
        isOpen={confirmOpen}
        setCardOpen={setConfirmOpen}
        message={confirmMessage}
        confirmAction={confirmAction}
      />
      <ThriveModal
        open={messageOpen}
        setOpen={setMessageOpen}
        closeByClick={true}
        height={250}
        width={500}
        contentComponent={
          <NoticeDialog
            title={messageTitle}
            body={messageBody}
            setOpen={setMessageOpen}
            dataFlag={dataFlag}
            setDataFlag={setDataFlag}
          ></NoticeDialog>
        }
      ></ThriveModal>
      <MergeCase
        open={mergeCaseOpen}
        setOpen={setMergeCaseOpen}
        currentCase={theCase}
        currentFirmName={firm["Company Name"]}
      />
    </>
  );
};

export default Case;
